import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { api, formatApiError } from "@/lib/api";
import { NIGERIAN_STATES } from "@/lib/constants";
import { lgasForState } from "@/lib/lga";
import { CheckCircle2, Loader2, Copy, ArrowRight, Salad, Wallet, SplitSquareHorizontal } from "lucide-react";

export default function Register() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    phone: "", full_name: "", dob: "", gender: "", state: "", lga: "", community: "",
    email: "", nin: "", preferred_state: "", benefit_pref: "food", pin: "", consent: false,
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Please consent to data processing to continue.");
    setLoading(true);
    try {
      const { data } = await api.post("/beneficiaries/register", form);
      if (data.duplicate) {
        toast.warning(data.message);
        setResult({ ...data, duplicate: true });
      } else {
        toast.success("Registration successful!");
        setResult(data);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    const b = result.beneficiary;
    return (
      <div>
        <Navbar />
        <div className="max-w-xl mx-auto px-4 py-16">
          <div className="rounded-3xl border border-emerald-200 bg-white p-8 text-center tmf-rise shadow-sm">
            <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto tmf-pulse">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 className="mt-5 font-display font-bold text-2xl text-emerald-950">
              {result.duplicate ? "Existing TMF Profile" : "Welcome to TEXT-ME-FOOD"}
            </h1>
            <p className="mt-2 text-sm text-gray-600">Your permanent TMF Beneficiary ID</p>
            <div className="mt-4 flex items-center justify-center gap-2">
              <span data-testid="registration-tmf-id" className="font-mono font-bold text-lg text-emerald-700 bg-emerald-50 px-4 py-2 rounded-xl">{b.tmf_id}</span>
              <button onClick={() => { navigator.clipboard.writeText(b.tmf_id); toast.success("Copied"); }} className="p-2 rounded-lg hover:bg-emerald-50">
                <Copy className="w-4 h-4 text-emerald-600" />
              </button>
            </div>
            {result.sms && (
              <div className="mt-6 text-left bg-emerald-950 rounded-2xl p-4">
                <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400 mb-1">SMS</div>
                <div className="text-sm text-emerald-50 font-mono leading-relaxed">{result.sms.message}</div>
              </div>
            )}
            <div className="mt-7 flex flex-col gap-2">
              <Button data-testid="goto-check-benefit-btn" onClick={() => navigate("/check")} className="bg-emerald-600 hover:bg-emerald-700 h-12 rounded-full">
                Access My Benefit Portal <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button variant="ghost" onClick={() => navigate("/")}>Back to Home</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const lgas = lgasForState(form.state);

  return (
    <div>
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 py-10 lg:py-14">
        <span className="text-xs font-mono uppercase tracking-widest text-emerald-700">Beneficiary Registration</span>
        <h1 className="mt-2 font-display font-bold text-3xl text-emerald-950">Register for TMF</h1>
        <p className="mt-2 text-sm text-gray-600">Only your mobile number is required. Other details are optional and programme-dependent.</p>

        <form onSubmit={submit} className="mt-8 space-y-6">
          <div className="rounded-2xl border border-emerald-100 bg-white p-6 space-y-4">
            <div>
              <Label>Mobile / GSM Number <span className="text-red-500">*</span></Label>
              <Input data-testid="reg-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="080XXXXXXXX" required className="mt-1.5 h-12" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Full Name</Label>
                <Input data-testid="reg-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="mt-1.5 h-12" />
              </div>
              <div>
                <Label>Date of Birth</Label>
                <Input data-testid="reg-dob" type="date" value={form.dob} onChange={(e) => set("dob", e.target.value)} className="mt-1.5 h-12" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>State</Label>
                <Select value={form.state} onValueChange={(v) => setForm((f) => ({ ...f, state: v, lga: "" }))}>
                  <SelectTrigger data-testid="reg-state" className="mt-1.5 h-12"><SelectValue placeholder="Select state" /></SelectTrigger>
                  <SelectContent>{NIGERIAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>LGA</Label>
                <Select value={form.lga} onValueChange={(v) => set("lga", v)} disabled={!form.state}>
                  <SelectTrigger data-testid="reg-lga" className="mt-1.5 h-12"><SelectValue placeholder={form.state ? "Select LGA" : "Select state first"} /></SelectTrigger>
                  <SelectContent>{lgas.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>NIN</Label>
                <Input data-testid="reg-nin" value={form.nin} onChange={(e) => set("nin", e.target.value)} className="mt-1.5 h-12" />
              </div>
              <div>
                <Label>Redemption PIN (optional)</Label>
                <Input data-testid="reg-pin" value={form.pin} onChange={(e) => set("pin", e.target.value)} placeholder="4-digit PIN" className="mt-1.5 h-12" />
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-emerald-100 bg-white p-6">
            <Label className="text-base">Preferred Redemption State</Label>
            <p className="text-xs text-gray-500 mt-1 mb-3">For planning & vendor discovery only — this does <b>not</b> restrict where you can redeem.</p>
            <Select value={form.preferred_state} onValueChange={(v) => set("preferred_state", v)}>
              <SelectTrigger data-testid="reg-preferred-state" className="h-12"><SelectValue placeholder="Where do you prefer to redeem?" /></SelectTrigger>
              <SelectContent>{NIGERIAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="rounded-2xl border border-emerald-100 bg-white p-6">
            <Label className="text-base">How would you prefer to receive your TMF Benefit?</Label>
            <p className="text-xs text-gray-500 mt-1 mb-4">Subject to what each sponsor programme permits.</p>
            <RadioGroup value={form.benefit_pref} onValueChange={(v) => set("benefit_pref", v)} className="grid sm:grid-cols-3 gap-3">
              {[
                { v: "food", icon: Salad, t: "Food / Groceries", d: "Eligible food & grocery items." },
                { v: "cashback", icon: Wallet, t: "Cashback", d: "Cash component via approved mechanism." },
                { v: "split", icon: SplitSquareHorizontal, t: "50% / 50%", d: "Split between food & cashback." },
              ].map((o) => (
                <label key={o.v} data-testid={`reg-benefit-${o.v}`} className={`cursor-pointer rounded-xl border-2 p-4 transition-all ${form.benefit_pref === o.v ? "border-emerald-500 bg-emerald-50" : "border-gray-200 hover:border-emerald-200"}`}>
                  <div className="flex items-center justify-between">
                    <o.icon className="w-5 h-5 text-emerald-600" />
                    <RadioGroupItem value={o.v} />
                  </div>
                  <div className="mt-2 font-semibold text-sm text-emerald-950">{o.t}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{o.d}</div>
                </label>
              ))}
            </RadioGroup>
          </div>

          <label className="flex items-start gap-3 rounded-2xl border border-emerald-100 bg-white p-5 cursor-pointer">
            <Checkbox data-testid="reg-consent" checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} className="mt-0.5" />
            <span className="text-sm text-gray-600">I consent to TMF processing my personal data to verify my identity and deliver benefits, in line with applicable Nigerian data-protection requirements.</span>
          </label>

          <Button data-testid="reg-submit" type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700 rounded-full h-12 text-base">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Complete Registration"}
          </Button>
        </form>
      </div>
    </div>
  );
}
